/**
 * CS 代行の委任トークンの受け渡し。
 *
 * オペレーター向けアプリ(`ops.`)で発行したトークンを、顧客向けアプリ(`app.`)の
 * メモリへ渡す。2 つは**別のオリジン**なので、直接は渡せない。
 *
 * ## URL に載せない
 *
 * クエリにもハッシュにもトークンを載せない。URL はブラウザの履歴に残り、
 * アドレス欄に出る(スクリーンショットにも写る)。代わりに `postMessage` で
 * 受け渡す。**オリジンは双方向で名指しして確かめる**。
 *
 * ## 段取り
 *
 * 1. オペレーター側が押した瞬間に空の窓を開く(押してから開くとポップアップ
 *    ブロッカーに止められる)。窓は受け取り待ちの画面を出す
 * 2. 受け取り側が「準備ができた」を `window.opener` へ送る
 * 3. オペレーター側が、その窓へトークンを送る(`targetOrigin` を名指し)
 * 4. 受け取り側は認可サーバーへ POST し、期限付き HttpOnly セッションを作る。
 *    PKCE のコード交換後、アクセストークンはメモリだけに持つ。
 *
 * 受け取り側は送り元のオリジンを、送り側は宛先のオリジンを確かめる。
 * どちらかが未設定の面では、この経路そのものを出さない(繋がっていない口を
 * 押せる形にしない)。
 */

/** 受け取り側 → 送り側。「窓の準備ができた」。 */
export const DELEGATION_HANDOFF_READY = "visumo.admin.delegation.ready";

/** 送り側 → 受け取り側。委任トークンそのもの。 */
export const DELEGATION_HANDOFF_TOKEN = "visumo.admin.delegation.token";

/** 受け取り側の画面のパス(オペレーター側はここを開く)。 */
export const DELEGATION_HANDOFF_PATH = "/delegation/start";

/** 受け渡しの取り決め時間。これを過ぎたら諦めて、押し直してもらう。 */
export const DELEGATION_HANDOFF_TIMEOUT_MS = 20_000;

export interface DelegationHandoffToken {
  readonly type: typeof DELEGATION_HANDOFF_TOKEN;
  readonly accessToken: string;
  readonly expiresIn: number;
}

/** 受け取ったメッセージが委任トークンの形か。**推測で補わない**。 */
export function readDelegationHandoffToken(
  data: unknown,
): { readonly accessToken: string; readonly expiresIn: number } | null {
  if (typeof data !== "object" || data === null) return null;
  const message = data as Record<string, unknown>;
  if (message["type"] !== DELEGATION_HANDOFF_TOKEN) return null;
  const accessToken = message["accessToken"];
  const expiresIn = message["expiresIn"];
  if (typeof accessToken !== "string" || accessToken.length === 0) return null;
  if (typeof expiresIn !== "number" || !Number.isFinite(expiresIn)) return null;
  return { accessToken, expiresIn };
}

/** 受け取ったメッセージが「準備ができた」か。 */
export function isDelegationHandoffReady(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as Record<string, unknown>)["type"] === DELEGATION_HANDOFF_READY
  );
}
